'use strict';

import EventEmitter from 'eventemitter3';
import JsCookies from 'js-cookie';

/**
 * Tracking consent management component
 * Handles cookie consent banner, user preferences, and tracking service initialization
 */
export default new class Tracking {
    /**
     * @type {Object} Custom events name
     */
    E = {
        REFUSE_TRACKING: 'refuse_tracking',
    };

    /**
     * @type {string} Tracking consent prefix
     */
    #TRACKING_CONSENT_PREFIX = 'GRAHOU_tracking_consent-';

    /**
     * @type {HTMLDialogElement} $popIn
     */
    #$popIn;

    /**
     * @type {HTMLElement} $form
     */
    #$form;

    /**
     * @type {HTMLElement} $formToggle
     */
    #$formToggle;

    /**
     * @type {HTMLElement} $servicesWrapper
     */
    #$servicesWrapper;

    /**
     * @type {NodeList} $$input
     */
    #$$input;

    /**
     * @type {HTMLElement} $btnRefuse
     */
    #$btnRefuse;

    /**
     * @type {HTMLElement} $btnAccept
     */
    #$btnAccept;

    /**
     * @type {HTMLElement} $btnEdit
     */
    #$btnEdit;

    /**
     * @type {boolean} Is the first opening of the pop-in?
     * Usefull to set the focus on HTML elements to improved keyboard navigation & add or remove services on init
     */
    #isFirstOpening = true;

    /**
     * @type {Object} Tracking JS values from DOM
     */
    #trackingValues = {};

    /**
     * @type {Object} SetTimeouts
     */
    sto = {};

    /**
     * @type {Object} SetIntervals
     */
    si = {};

    /**
     * Initialize the tracking component
     * Only initializes if tracking consent pop-in element exists in the DOM
     * @return {void}
     */
    init() {
        this.#$popIn = document.getElementById('tracking_consent_pop_in');

        if (!this.#$popIn) {
            return;
        }

        this.initDOM();
        this.initEl();
        this.bindEvents();
    }

    /**
     * Initialize DOM elements
     * Caches references to tracking consent UI elements
     * @return {void}
     */
    initDOM() {
        this.#$form = this.#$popIn.querySelector('.tracking_consent_pop_in--form');
        this.#$formToggle = this.#$popIn.querySelector('.tracking_consent_pop_in--form-services_toggle');
        this.#$servicesWrapper = this.#$popIn.querySelector('.tracking_consent_pop_in--form-services_wrapper');
        this.#$$input = this.#$popIn.querySelectorAll('.tracking_consent_pop_in--form-service-input');
        this.#$btnRefuse = this.#$popIn.querySelector('#tracking_consent_pop_in--btn__refuse');
        this.#$btnAccept = this.#$popIn.querySelector('#tracking_consent_pop_in--btn__accept');
        this.#$btnEdit = document.body.querySelector('button[popovertarget="tracking_consent_pop_in"]');
    }

    /**
     * Initialize element state
     * Checks existing cookies and applies appropriate consent settings
     * @return {void}
     */
    initEl() {
        this.destroyTimer();

        Dom.inert(this.#$servicesWrapper);

        this.#setTrackingValues();
        this.#checkCookies();
    }

    /**
     * Bind event listeners to UI elements
     * Sets up click handlers for consent actions
     * @return {void}
     */
    bindEvents() {
        this.#$formToggle.addEventListener('click', this.#handleServicesOpening.bind(this));
        this.#$btnRefuse.addEventListener('click', this.#saveCookies.bind(this));
        this.#$btnAccept.addEventListener('click', this.#saveCookies.bind(this));
        this.#$form.addEventListener('submit', this.#submitForm.bind(this));
    }

    /**
     * Set tracking JS values get from the DOM
     * @private
     * @return {void}
     */
    #setTrackingValues() {
        this.#trackingValues.services = {};
        window.GRAHOU_tracking.services.forEach((service) => {
            this.#trackingValues.services[service.slug] = service;
        });
        this.#trackingValues.expirationDays = Number(window.GRAHOU_tracking.expirationDays);
    }

    /**
     * Check existing consent cookies and initialize tracking services
     * Shows consent banner if no consent decision has been made
     * Add accepted tracking services based on stored cookies
     * @private
     * @return {void}
     */
    #checkCookies() {
        if (JsCookies.get(this.#TRACKING_CONSENT_PREFIX + 'submitted') === undefined) {
            this.#showPopIn();
        }
        else {
            Object.values(this.#trackingValues.services).forEach((service) => {
                if (this.getCookieAcceptance(service.slug)) {
                    this.#addService(service);
                }
            });

            this.#isFirstOpening = false;

            this.#updateCheckboxes();
        }
    }

    /**
     * Handle form opening
     * Opens or closes the tracking details checkboxes
     * @private
     * @return {void}
     */
    #handleServicesOpening() {
        Dom.inert(this.#$servicesWrapper, this.#$form.classList.contains('open'));
        this.#$form.classList.toggle('open');
    }

    /**
     * Save the acceptance of each cookie depending on checkbox checked or accept value given
     * Hides consent banner, sets acceptance cookies, and initializes selected tracking services
     * @param {boolean|null} accept Are all cookies accepted?
     * @private
     * @return {void}
     */
    #saveCookies(accept) {
        if (this.#isFirstOpening) {
            this.#isFirstOpening = false;
        }
        else {
            this.#$btnEdit.focus();
        }

        JsCookies.set(this.#TRACKING_CONSENT_PREFIX + 'submitted', '1', {
            expires: this.#trackingValues.expirationDays,
        }); // set the cookie for tracking consent submitted

        // Add or remove cookies for associated services
        if (this.#trackingValues?.services && this.#trackingValues.services.constructor === Object) {
            Object.values(this.#trackingValues.services).forEach((service) => {
                if (
                    (accept === null && document.querySelector('#tracking_consent-' + service.slug).checked) || // from submit form
                    accept === true // from accept all button
                ) {
                    this.#addService(service);
                }
                // from submit form or remove all button
                else {
                    this.#removeService(service);
                }
            });

            this.#updateCheckboxes();
        }

        this.#hidePopIn();
    }

    /**
     * Submit form
     * @param {Event} e Event
     * @private
     * @return {void}
     */
    #submitForm(e) {
        e.preventDefault();

        this.#saveCookies(null);
    }

    /**
     * Show the consent pop-in to allow users to modify their preferences
     * @private
     * @return {void}
     */
    #showPopIn() {
        this.#$popIn.showPopover();
    }

    /**
     * Show the consent pop-in
     * @private
     * @return {void}
     */
    #hidePopIn() {
        this.#$popIn.hidePopover();
    }

    /**
     * Add tracking service
     * @param {Object} service Service object containing consent parameters
     * @private
     * @return {void}
     */
    #addService(service) {
        if (!this.#isFirstOpening && this.getCookieAcceptance(service.slug)) {
            return;
        }

        if (service.type === 'gtag') {
            this.#updateGTAG(service, 'granted'); // if gtag, send granted
        }
        else if (service.type === 'matomo') {
            this.#updateMatomo('rememberCookieConsentGiven');
        }
        else if (service.type === 'custom') {
            this.#addScript(service);
        }

        this.setCookieAcceptance(service.slug);
    }

    /**
     * Remove tracking service
     * @param {Object} service Service object containing consent parameters
     * @private
     * @return {void}
     */
    #removeService(service) {
        if (!this.#isFirstOpening && !this.getCookieAcceptance(service.slug)) {
            return;
        }

        if (service.type === 'gtag') {
            this.#updateGTAG(service, 'denied'); // if gtag, send denied
        }
        else if (service.type === 'matomo') {
            this.#updateMatomo('forgetCookieConsentGiven');
        }
        else if (service.type === 'custom') {
            this.#removeScript(service);
        }

        this.#removeCookieAcceptance(service.slug);
    }

    /**
     * Update Google Tag Manager consent status
     * Updates consent parameters for GTAG services
     * @private
     * @param {Object}        service            Service object containing consent parameters
     * @param {Array<string>} service.consent    Array of consent parameter names
     * @param {string}        [status='granted'] Consent status ('granted' or 'denied')
     * @return {void}
     */
    #updateGTAG(service, status = 'granted') {
        const updatedParams = {};
        for (const updatedParam of service.consent) {
            updatedParams[updatedParam] = status;
        }
        if (typeof gtag !== 'undefined') {
            gtag('consent', 'update', updatedParams); // eslint-disable-line no-undef
        }
    }

    /**
     * Update Matomo consent status
     * @private
     * @param {string} [status='rememberCookieConsentGiven'] Matomo consent method ('rememberCookieConsentGiven' or 'forgetCookieConsentGiven')
     * @return {void}
     */
    #updateMatomo(status = 'rememberCookieConsentGiven') {
        if (typeof _paq !== 'undefined') {
            _paq.push([status]); // eslint-disable-line no-undef
        }
    }

    /**
     * Add a custom tracking script
     * Appends the service script to the document body
     * @private
     * @param {Object} service        Service object containing script information
     * @param {string} service.script HTML script tag to be loaded
     * @return {void}
     */
    #addScript(service) {
        document.body.append(document.createRange().createContextualFragment(service.script));
    }

    /**
     * Remove a custom tracking script
     * Remove the service script from the document body
     * @private
     * @param {Object} service      Service object containing script information
     * @param {string} service.slug Service slug
     * @return {void}
     */
    #removeScript(service) {
        this.emit(this.E.REFUSE_TRACKING, service.slug);
        document.getElementById('GRAHOU_tracking_service_script-' + service.slug).remove();
    }

    /**
     * Set cookie for acceptance of tracking service
     * @public
     * @param {string} serviceSlug Tracking service slug
     * @return {void}
     */
    setCookieAcceptance(serviceSlug) {
        JsCookies.set(this.#TRACKING_CONSENT_PREFIX + serviceSlug + '_accepted', '1', {
            expires: this.#trackingValues.expirationDays,
        });
    }

    /**
     * Get cookie for acceptance of tracking service
     * @public
     * @param {string} serviceSlug Tracking service slug
     * @return {boolean} Return if the service cookie exist
     */
    getCookieAcceptance(serviceSlug) {
        return JsCookies.get(this.#TRACKING_CONSENT_PREFIX + serviceSlug + '_accepted') !== undefined;
    }

    /**
     * Remove cookie for acceptance of tracking service
     * @public
     * @param {string} serviceSlug Tracking service slug
     * @return {void}
     */
    #removeCookieAcceptance(serviceSlug) {
        JsCookies.remove(this.#TRACKING_CONSENT_PREFIX + serviceSlug + '_accepted');
    }

    /**
     * Update the checkboxes state
     * @param {string|null} serviceSlug Tracking service slug, used to check a specific checkbox from his service slug
     * @public
     * @return {void}
     */
    #updateCheckboxes(serviceSlug = null) {
        this.#$$input.forEach(($el) => {
            const inputServiceSlug = $el.getAttribute('data-service_slug');
            const serviceCookieAcceptance = this.getCookieAcceptance(inputServiceSlug);
            if (
                (serviceSlug === null && serviceCookieAcceptance) || // not for a specific checkbox & the service cookie acceptance is set
                serviceSlug === inputServiceSlug // for a specific checkbox
            ) {
                $el.setAttribute('checked', '');
                $el.checked = true;
            }
            else if (serviceSlug === null && !serviceCookieAcceptance) {
                // not for a specific checkbox & the service cookie acceptance is not set
                $el.removeAttribute('checked');
                $el.checked = false;
            }
        });
    }

    /**
     * Add tracking service from external class (flexible for example)
     * @param {string} serviceSlug Tracking service slug
     * @public
     * @return {void}
     */
    addTrackingService(serviceSlug) {
        const service = this.#trackingValues?.services[serviceSlug];
        if (service) {
            this.#addService(service);
            this.#updateCheckboxes(serviceSlug); // update the specific checkbox
        }
    }

    /**
     * Destroy all the setTimeout calls and the setInterval calls
     */
    destroyTimer() {
        /* setTimeout */
        for (const sto in this.sto) {
            clearTimeout(this.sto[sto]);
        }

        /* setInterval */
        for (const si in this.si) {
            clearInterval(this.si[si]);
        }
    }

    /**
     * Bind a custom event
     *
     * @param {string}   name    The name of the event
     * @param {Function} fct     The callback function to call
     * @param {Object}   context Optionnaly, a context to tie the callback
     */
    on(name, fct, context) {
        if (this.e == null) {
            // if the custom event doesn't exist, create it
            this.e = new EventEmitter();
        }

        this.e.on(name, fct, context); // add the listener to the custom event
    }

    /**
     * Emit a custom event
     *
     * @param {string} name   The name of the event
     * @param {...any} params Additionnal optionnal parameters to pass to the callback
     */
    emit(name, ...params) {
        if (this.e !== null) {
            this.e.emit(name, ...params);
        }
    }
}();

class Dom {
    /**
     * Return position coordinates of an HTML element
     *
     * @param {HTMLElement} $el
     * @return {{top: number, left: number}} Top & left coordinates
     */
    static position($el) {
        const offset = this.offset($el);
        const parentOffset = this.offset($el.parentNode);

        return {
            top: offset.top - parentOffset.top,
            left: offset.left - parentOffset.left,
        };
    }

    /**
     * Return offset coordinates of an HTML element
     *
     * @param {HTMLElement} $el
     * @return {{top: number, left: number}} Top & left coordinates
     */
    static offset($el) {
        const { top, left } = $el.getBoundingClientRect();

        return {
            top: top + window.scrollY,
            left: left + window.scrollX,
        };
    }

    /**
     * Inert HTML element
     *
     * @param {HTMLElement|NodeList} $els
     * @param {boolean}              value
     */
    static inert($els, value = true) {
        if ($els === undefined || $els === null) {
            // eslint-disable-next-line no-console
            console.warn(`You're trying to inert a "${$els}" element.`);
            return;
        }

        if (HTMLElement.prototype.isPrototypeOf($els)) {
            $els.inert = value;
        }
        else if ($els !== null) {
            $els.forEach(($el) => {
                if ($el === undefined || $el === null) {
                    // eslint-disable-next-line no-console
                    console.warn(`You're trying to inert a "${$el}" element.`);
                    return;
                }
                $el.inert = value;
            });
        }
    }

    /**
     * Select next element
     *
     * @param {HTMLElement} $el
     * @param {string}      selector
     */
    static next($el, selector) {
        const $nextEl = $el.nextElementSibling;

        if (!selector || ($nextEl && $nextEl.matches(selector))) {
            return $nextEl;
        }

        return null;
    }

    /**
     * Select parents element
     *
     * @param {HTMLElement} $el
     * @param {string}      selector
     * @return {null|HTMLElement|HTMLElement[]} HTMLElement or array of HTMLElement
     */
    static parents($el, selector) {
        const parents = [];

        while (($el = $el.parentNode) && $el !== document) {
            if (!selector || $el.matches(selector)) {
                parents.push($el);
            }
        }

        if (parents.length === 0) {
            return null;
        }

        if (parents.length === 1) {
            return parents[0];
        }

        return parents;
    }

    /**
     * Trigger native event
     *
     * @param {HTMLElement} $el
     * @param {string}      eventName
     */
    static trigger($el, eventName) {
        if (typeof eventName === 'string' && typeof $el[eventName] === 'function') {
            $el[eventName]();
        }
        else {
            const event = typeof eventName === 'string' ? new Event(eventName, { bubbles: true }) : eventName;
            $el.dispatchEvent(event);
        }
    }
}

